import React from "react";
import { Table, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import VehicleDetailCard from "./VehicleDetailCard";

const { Title } = Typography;

const KEY_FEATURES = ["standard"];

const VehicleBasicInformation = ({ data }) => {
  const columns = [
    {
      dataIndex: "field",
      key: "field",
      width: "40%",
      className: "bg-[#fafafa] text-sm pl-4 text-black/[0.65] font-normal",
    },
    {
      dataIndex: "value",
      key: "value",
      width: "60%",
      className: "text-black/[0.88] pl-4 text-sm font-normal",
    },
  ];

  return (
    <div className="mb-4">
      <Title level={5} className="!text-black/[0.88] !text-base !font-bold !mb-4">
        Basic Information
      </Title>
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        showHeader={false}
        size="small"
        rowKey="field"
        bordered
        className="w-full"
      />
    </div>
  );
};

const VehicleKeyFeatures = ({ features }) => {
  if (!features || features.length === 0) {
    return <div className="text-center text-gray-500">No key features available</div>;
  }

  return (
    <div className="mb-4">
      <Title level={5} className="!text-black/[0.88] !text-base !font-bold !mb-4">
        Key Features
      </Title>
      <div className="grid grid-cols-2 gap-4">
        {features.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-3 bg-[#FAFAFA] rounded-lg p-3 border border-solid border-[#F0F0F0]"
          >
            <CheckOutlined className="!text-black/[0.45] flex-shrink-0" />
            <p className="m-0 text-sm text-black/[0.88]">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const VehicleMoreDetails = ({ vehicle }) => {
  // Map Murdock location vehicle data
  const vehicleData = {
    images: vehicle?.list_of_photos?.map(photo => photo.PhotoUrl) || [
      "https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png",
    ],
    year: vehicle?.year,
    make: vehicle?.make,
    model: vehicle?.model,
    trim: vehicle?.trim,
    msrp: vehicle?.msrp || vehicle?.selling_price,
    vdpLink: vehicle?.vdp_url,
    location: vehicle?.vehicle_info?.DealerName,
  };

  // Normalize basic information data
  const fuelEconomy =
    vehicle?.mpg_city && vehicle?.mpg_highway
      ? `${vehicle.mpg_city} city / ${vehicle.mpg_highway} hwy ${vehicle?.epa_unit || "MPG"}`
      : vehicle?.epa_combined || "N/A";

  const basicInfoData = [
    { field: "VIN", value: vehicle?.vin || "N/A" },
    { field: "Stock Number", value: vehicle?.stock_number || "N/A" },
    { field: "Model No", value: vehicle?.model_number || "N/A" },
    { field: "Condition", value: vehicle?.is_new ? "New" : "Used" },
    { field: "Year", value: vehicle?.year || "N/A" },
    { field: "Make", value: vehicle?.make || "N/A" },
    { field: "Model", value: vehicle?.model || "N/A" },
    { field: "Trim", value: vehicle?.trim || "N/A" },
    { field: "Exterior Colour", value: vehicle?.exterior_color || "N/A" },
    { field: "Interior Colour", value: vehicle?.interior_color || "N/A" },
    {
      field: "Mileage",
      value: vehicle?.mileage
        ? `${vehicle.mileage.toLocaleString()} ${vehicle?.mileage_unit || "mi"}`
        : "N/A",
    },
    { field: "Fuel Economy", value: fuelEconomy },
    { field: "Drivetrain", value: vehicle?.drive_train || "N/A" },
    { field: "Body Type", value: vehicle?.body_style || "N/A" },
    { field: "Doors", value: vehicle?.vehicle_info?.DoorCount || "N/A" },
    { field: "Fuel Type", value: vehicle?.fuel_type || "N/A" },
    { field: "Transmission", value: vehicle?.transmission || "N/A" },
    { field: "Engine", value: vehicle?.vehicle_info?.Engine || "N/A" },
  ];

  // Extract priority features from list_of_options
  const keyFeatures =
    vehicle?.list_of_options
      ?.filter(opt => KEY_FEATURES.includes(opt.Type?.toLowerCase()))
      .map(opt => opt.Description) || [];

  return (
    <div>
      <VehicleDetailCard
        images={vehicleData.images}
        year={vehicleData.year}
        make={vehicleData.make}
        model={vehicleData.model}
        trim={vehicleData.trim}
        msrp={vehicleData.msrp}
        vdpLink={vehicleData.vdpLink}
        location={vehicleData.location}
      />

      <div className="mt-8">
        <VehicleBasicInformation data={basicInfoData} />
      </div>

      <div className="mt-8">
        <VehicleKeyFeatures features={keyFeatures} />
      </div>
    </div>
  );
};

export default VehicleMoreDetails;
